import * as d3 from 'd3';
import _ from 'lodash';

import {topoUtils} from './topo-utils';

const stateCentroidOffsets = {
  "12": [1.024, 1.02], //FL
  "26": [1.022, 1.18], //MI
  "15": [1.05, 1.03],  //HI
  "34": [1.005, 1],    //NJ
  "24": [.995, .99],   //MD
  "21": [1, 1.02],     //KY
  "50": [.999, .97],   //VT
  "33": [1, 1.03],     //NH
  "22": [0.98, 1],     //LA
  "16": [1, 1.1],      //ID
  "6": [0.9, 1.01]     //CA
};

class GeoUtils {

  constructor() {
    this.cache = {};
  }

  /**
   * Create a geoAlbersUsa projection and geo path
   * with the map centered and scaled to fit.
   */
  getAlbersUsaPath(width, height) {
    const projection = d3.geoAlbersUsa()
      .translate([width / 2, height / 2.04])
      .scale(width * 1.22);

    return d3.geoPath().projection(projection);
  }

  getAlbersStatePathProjection(stateFips, width, height) {
    const topo = topoUtils.getStateTopography(stateFips);
    const projection = stateFips === 2 ? d3.geoAlbersUsa() : d3.geoMercator();
    const path = d3.geoPath().projection(projection);

    projection.scale(1).translate([0, 0]);

    const b = path.bounds(topo.land),
      s = .95 / Math.max((b[1][0] - b[0][0]) / width, (b[1][1] - b[0][1]) / height),
      t = [(width - s * (b[1][0] + b[0][0])) / 2, (height - s * (b[1][1] + b[0][1])) / 2];

    projection.scale(s).translate(t);

    return {
      path,
      projection
    };
  }

  getAlbersStatePath(stateFips, width, height) {
    return this.getAlbersStatePathProjection(stateFips, width, height).path;
  }

  getAlbersStateProjection(stateFips, width, height) {
    return this.getAlbersStatePathProjection(stateFips, width, height).projection;
  }

  offsetStateCentroid(stateFips, point) {
    const offset = stateCentroidOffsets[stateFips];

    if (offset) {
      return [
        point[0] * offset[0],
        point[1] * offset[1]
      ]
    }

    return point;
  };

  getMercatorStateCountyPaths(stateFips, width, height) {
    const key = `mercator-state-${stateFips}- ${Math.round(width)}-${Math.round(height)}`;

    if (!this.cache[key]) {
      const topo = topoUtils.getStateTopography(stateFips);
      const path = this.getAlbersStatePath(stateFips, width, height);
      this.cache[key] = _.map(topo.counties, d => {
        return {
          id: d.id,
          countyFips: d.id,
          stateFips: Math.floor(d.id / 1000),
          path: path(d),
          cent: path.centroid(d)
        }
      });
    }

    return this.cache[key] || {};
  }

  getAlbersUsaCountyPaths(width, height) {
    const key = `albers-county-${Math.round(width)}-${Math.round(height)}`;

    if (!this.cache[key]) {
      const topo = topoUtils.getNationalTopography();
      const path = this.getAlbersUsaPath(width, height);
      this.cache[key] = _.map(topo.counties, d => {
        return {
          id: d.id,
          countyFips: d.id,
          stateFips: Math.floor(d.id / 1000),
          path: path(d),
          cent: path.centroid(d)
        }
      });
    }

    return this.cache[key] || {};
  }

  getAlbersUsaStatePaths(width, height) {
    const key = `albers-state-${Math.round(width)}-${Math.round(height)}`;

    if (!this.cache[key]) {
      const topo = topoUtils.getNationalTopography();
      const path = this.getAlbersUsaPath(width, height);
      this.cache[key] = _.map(topo.states, d => {
        return {
          id: d.id,
          stateFips: Math.floor(d.id / 1000),
          path: path(d),
          cent: this.offsetStateCentroid(d.id, path.centroid(d))
        }
      });
    }

    return this.cache[key] || {};
  }

  createBivariateScale(data, dimX, dimY, colorGrid) {
    const bivarColors = colorGrid || [
      ['#e8e8e8', '#e4acac', '#c85a5a'],
      ['#b0d5df', '#ad9ea5', '#985356'],
      ['#64acbe', '#627f8c', '#574249']
    ];

    const valueX = row => _.get(row, dimX, 0);
    const valueY = row => _.get(row, dimY, 0);

    const valuesX = _.map(data, valueX);
    const valuesY = _.map(data, valueY);

    const scaleX = d3.scaleQuantile()
      .domain(valuesX)
      .range([0, 1, 2]);

    const scaleY = d3.scaleQuantile()
      .domain(valuesY)
      .range([0, 1, 2]);

    return function (row) {
      const iX = dimX ? scaleX(valueX(row)) : 0;
      const iY = dimY ? scaleY(valueY(row)) : 0;

      return _.get(bivarColors, [iY, iX], bivarColors[0][0]);
    }
  }

}

export let geoUtils = new GeoUtils();


// WEBPACK FOOTER //
// ./src/charts/utils/geo-utils.js